import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Loader2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAdmin } from "@/contexts/AdminContext";
import { useSettings, buildWhatsAppLink, onWhatsAppClick } from "@/contexts/SettingsContext";
import WhatsAppIcon from "@/components/WhatsAppIcon";

const SS_KEY = "bda_lead_popup_v1";  // values: "submitted" | "dismissed"
const TRIGGER_DELAY_MS = 20000;

export default function LeadCapturePopup() {
  const location = useLocation();
  const isAdminPath = location.pathname.startsWith("/admin");
  const { isAdmin } = useAdmin();
  const { settings } = useSettings();
  const skip = isAdminPath || isAdmin;

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Trigger popup after 20s (skip on admin paths and for logged-in admins)
  useEffect(() => {
    if (skip) return;
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SS_KEY)) return;
    const t = setTimeout(() => setOpen(true), TRIGGER_DELAY_MS);
    return () => clearTimeout(t);
  }, [skip]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) e.email = "Enter a valid email";
    const digits = form.whatsapp.replace(/[^\d]/g, "");
    if (digits.length < 7) e.whatsapp = "Enter a valid WhatsApp number with country code";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const dismiss = () => {
    setOpen(false);
    try { sessionStorage.setItem(SS_KEY, "dismissed"); } catch { /* ignore */ }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/leads", {
        name: form.name.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        page_url: typeof window !== "undefined" ? window.location.href : (location.pathname + location.search),
      });

      const sourceUrl = typeof window !== "undefined" ? window.location.href : "(BuildDirect Africa)";
      const ts = new Date().toLocaleString();
      const message =
        `Hello BuildDirect Africa, I am interested in your services.\n` +
        `Name: ${form.name.trim()}\n` +
        `Email: ${form.email.trim()}\n` +
        `WhatsApp: ${form.whatsapp.trim()}\n` +
        `Source: ${sourceUrl}\n` +
        `Sent: ${ts}`;
      const waUrl = buildWhatsAppLink(settings?.whatsapp_number, message);

      try { sessionStorage.setItem(SS_KEY, "submitted"); } catch { /* ignore */ }
      setSuccess(true);
      toast.success("Opening WhatsApp…");

      if (waUrl !== "#") {
        // Trigger the SAME bullet-proof flow used by every WhatsApp anchor:
        // mobile → native whatsapp:// scheme directly (no intermediate page),
        // desktop → new tab. Uses a synthetic anchor so the existing handler
        // path is shared.
        const tmp = document.createElement("a");
        tmp.href = waUrl;
        tmp.target = "_blank";
        tmp.rel = "noopener noreferrer";
        // Synthetic event so onWhatsAppClick can read currentTarget.href
        const fakeEvent = {
          currentTarget: tmp,
          preventDefault() {},
          stopPropagation() {},
        };
        onWhatsAppClick(fakeEvent);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn't submit — please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (skip) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="lead-popup"
          aria-modal="true" role="dialog"
        >
          {/* Subtle backdrop, NOT click-blocking outside the card explicitly */}
          <button
            type="button"
            aria-label="Close"
            onClick={dismiss}
            className="absolute inset-0 bg-black/55 backdrop-blur-sm"
          />

          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            className="relative w-full sm:max-w-md mx-3 sm:mx-auto bg-[color:var(--bda-surface)] border border-[color:var(--bda-border)] rounded-t-2xl sm:rounded-2xl shadow-[0_24px_60px_-12px_rgba(0,0,0,0.6)] overflow-hidden"
          >
            {/* Close */}
            <button
              type="button"
              onClick={dismiss}
              data-testid="lead-popup-close"
              aria-label="Close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full inline-flex items-center justify-center text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)] hover:bg-[color:var(--bda-surface-2)] transition-colors"
            >
              <X className="w-4 h-4" strokeWidth={1.6} />
            </button>

            <div className="p-6 sm:p-7">
              {/* Accent bar */}
              <div className="w-10 h-[2px] bg-[color:var(--bda-blue-glow)] mb-4" />

              <h3 className="font-heading text-[20px] sm:text-[22px] font-light text-[color:var(--bda-text)] tracking-refined leading-tight" data-testid="lead-popup-title">
                Would you like us to help with your project?
              </h3>
              <p className="mt-2 text-[13px] text-[color:var(--bda-text-muted)] leading-relaxed" data-testid="lead-popup-subtext">
                Drop your details and we'll continue the conversation on WhatsApp.
              </p>

              {success ? (
                <div className="mt-6 mb-2 flex items-center gap-3 text-[color:var(--bda-text)]" data-testid="lead-popup-success">
                  <span className="w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-400 inline-flex items-center justify-center">
                    <Check className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <div className="text-sm">Got it — we'll be in touch shortly.</div>
                </div>
              ) : (
                <form onSubmit={submit} className="mt-5 space-y-3" noValidate>
                  <Field
                    label="Name"
                    error={errors.name}
                    input={(
                      <input
                        type="text"
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        data-testid="lead-popup-name"
                        className="w-full px-4 py-2.5 bg-[color:var(--bda-surface-2)] border border-[color:var(--bda-border)] focus:border-[color:var(--bda-blue-glow)] rounded-md text-sm text-[color:var(--bda-text)] outline-none transition-colors"
                      />
                    )}
                  />
                  <Field
                    label="Email"
                    error={errors.email}
                    input={(
                      <input
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        data-testid="lead-popup-email"
                        className="w-full px-4 py-2.5 bg-[color:var(--bda-surface-2)] border border-[color:var(--bda-border)] focus:border-[color:var(--bda-blue-glow)] rounded-md text-sm text-[color:var(--bda-text)] outline-none transition-colors"
                      />
                    )}
                  />
                  <Field
                    label="WhatsApp number"
                    error={errors.whatsapp}
                    input={(
                      <input
                        type="tel"
                        autoComplete="tel"
                        value={form.whatsapp}
                        placeholder="+234"
                        onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                        data-testid="lead-popup-whatsapp"
                        className="w-full px-4 py-2.5 bg-[color:var(--bda-surface-2)] border border-[color:var(--bda-border)] focus:border-[color:var(--bda-blue-glow)] rounded-md text-sm text-[color:var(--bda-text)] outline-none transition-colors"
                      />
                    )}
                  />

                  {/* Premium WhatsApp green submit bar */}
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="lead-popup-submit"
                    className="mt-2 w-full inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1FB855] active:bg-[#1aa14a] text-white px-5 py-3.5 text-[14px] font-semibold tracking-wide rounded-full transition-all shadow-[0_8px_24px_-6px_rgba(37,211,102,0.55)] hover:shadow-[0_12px_30px_-6px_rgba(37,211,102,0.75)] disabled:opacity-60 disabled:shadow-none"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <WhatsAppIcon className="w-5 h-5" color="#FFFFFF" />
                        Submit to WhatsApp
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={dismiss}
                    data-testid="lead-popup-cancel"
                    className="w-full text-center bg-transparent hover:bg-[color:var(--bda-surface-2)] text-[color:var(--bda-text-muted)] hover:text-[color:var(--bda-text)] px-4 py-2 text-[12px] font-medium tracking-wide rounded-full transition-colors"
                  >
                    Maybe later
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, error, input }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[color:var(--bda-text-dim)] mb-1.5">{label}</div>
      {input}
      {error && <div className="mt-1 text-[11px] text-rose-400" role="alert">{error}</div>}
    </label>
  );
}
